import { Injectable } from '@angular/core';
import { KeyValue } from '@angular/common';
import { KeyValueExtended } from '../models/key-value-extended';
import { AldenService } from './alden.service';

@Injectable({
  providedIn: 'root'
})
export class SMTDataListsService {

  constructor(private aldenService: AldenService) {
  }

  /**
   * Busca elementos dentro de un Data List de Alfresco aplicando un término de búsqueda.
   *
   * Este método consulta un Data List identificado por `datalistName` y filtra sus
   * elementos utilizando el término proporcionado. La búsqueda se realiza sobre la
   * propiedad indicada (`searchProperty`) o sobre la clave por defecto si no se
   * especifica ninguna. Permite además:
   *
   *  - Realizar coincidencia exacta o parcial (`searchExactTerm`).
   *  - Ordenar los resultados por la clave del elemento (`sortByKey`).
   *
   * Devuelve una lista de pares clave‑valor extendidos (`KeyValueExtended`) que
   * representan los elementos coincidentes del Data List.
   *
   * @param datalistName Nombre del Data List de Alfresco a consultar.
   * @param searchTerm Término de búsqueda introducido por el usuario.
   * @param searchProperty (Opcional) Propiedad del elemento sobre la que se filtra.
   * @param searchExactTerm (Opcional) Si true, la coincidencia debe ser exacta.
   * @param sortByKey (Opcional) Si true, ordena los resultados por la clave.
   * @param extraData (Opcional) Metadatos adicionales que se quieren obtener.
   * @returns Promise con la lista filtrada de elementos del Data List.
   */
  searchTerm(datalistName: string, searchTerm: string, searchProperty?: string, searchExactTerm?: boolean, sortByKey?: boolean, extraData?: string[]): Promise<KeyValueExtended<string, string>[]> {
    const params = new URLSearchParams();
    if (datalistName) params.append('datalistName', datalistName);
    if (searchTerm) params.append('searchTerm', searchTerm);
    params.append('searchProperty', searchProperty ? searchProperty : "smtdl:value");
    params.append('searchExactTerm', searchExactTerm ? String(searchExactTerm) : "false");
    params.append('sortByKey', sortByKey ? String(sortByKey) : "false");
    if (extraData) params.append('extraData', String(extraData));

    const path = `smtecno/ssm/lists/items?${params.toString()}`;
    const httpMethod = 'GET';
    return this.aldenService.callAlfresco(path, httpMethod);
  }

  /**
   * Busca un elemento dentro de un Data List de Alfresco utilizando su clave.
   *
   * Este método consulta un Data List identificado por `datalistName` y localiza
   * el elemento cuya clave coincide exactamente con el valor proporcionado en `key`.
   * Devuelve una lista de pares clave‑valor (`KeyValue`) correspondientes a los
   * elementos encontrados, aunque normalmente la clave es única y el resultado
   * contendrá un único elemento.
   *
   * @param key Clave del elemento que se desea buscar dentro del Data List.
   * @param datalistName Nombre del Data List de Alfresco donde realizar la búsqueda.
   * @returns Promise con los elementos coincidentes en formato clave‑valor.
   */
  searchKey(key: string, datalistName: string): Promise<KeyValue<string, string>[]> {
    const params = new URLSearchParams();
    if (key) params.append('searchTerm', key);
    if (datalistName) params.append('datalistName', datalistName);
    params.append('searchProperty', "smtdl:key");
    params.append('searchExactTerm', "true");
    params.append('sortByKey', "true");

    const path = `smtecno/ssm/lists/items?${params.toString()}`;
    const httpMethod = 'GET';
    return this.aldenService.callAlfresco(path, httpMethod);
  }

    /**
   * Busca un elemento dentro de un Data List de Alfresco utilizando su clave.
   *
   * Este método consulta los elementos de un tipo `searchType` y localiza
   * el elemento cuya clave coincide exactamente con el valor proporcionado en `key`.
   * Devuelve una lista de pares clave‑valor (`KeyValue`) correspondientes a los
   * elementos encontrados, aunque normalmente la clave es única y el resultado
   * contendrá un único elemento.
   *
   * @param key Clave del elemento que se desea buscar dentro del Data List.
   * @param searchType Nombre del Data List de Alfresco donde realizar la búsqueda.
   * @returns Promise con los elementos coincidentes en formato clave‑valor.
   */
  searchKeyByType(key: string, searchType: string, extraData?: string[]): Promise<KeyValueExtended<string, string>[]> {
    const params = new URLSearchParams();
    if (key) params.append('searchTerm', key);
    if (searchType) params.append('searchType', searchType);
    params.append('searchProperty', "smtdl:key");
    params.append('searchExactTerm', "true");
    params.append('sortByKey', "true");
    if (extraData) params.append('extraData', extraData.toString());

    const path = `smtecno/ssm/lists/items?${params.toString()}`;
    const httpMethod = 'GET';
    return this.aldenService.callAlfresco(path, httpMethod);
  }

  /**
   * Obtiene el valor asociado a una clave dentro de un Data List o tipo de nodo de Alfresco.
   *
   * Este método busca un elemento cuyo identificador (`key`) coincida con el valor
   * proporcionado, dentro del tipo de nodo especificado (`nodeType`). En caso de que
   * el Data List esté estructurado jerárquicamente, puede filtrarse además por la
   * clave del elemento padre (`parentKey`).
   *
   * Devuelve una lista de pares clave‑valor (`KeyValue`) correspondientes a los
   * elementos encontrados. Normalmente la clave es única, por lo que el resultado
   * suele contener un único elemento.
   *
   * @param key Clave del elemento que se desea localizar.
   * @param nodeType Tipo de nodo o Data List donde realizar la búsqueda.
   * @param parentKey (Opcional) Clave del elemento padre para filtrar resultados.
   * @param masterKey (Opcional) Clave del elemento Principal para filtrar resultados.
   * @returns Promise con los elementos coincidentes en formato clave‑valor.
   */
  getValueOfKey(key: string, nodeType: string, parentKey?: string, masterKey?: string): Promise<KeyValue<string, string>[]> {
    const params = new URLSearchParams();
    if (key) params.append('searchTerm', key);
    if (nodeType) params.append('searchType', nodeType);
    if (parentKey) params.append('parentKey', parentKey);
    if (masterKey) params.append('masterKey', masterKey);
    params.append('searchProperty', "smtdl:key");
    params.append('searchExactTerm', "true");

    const path = `smtecno/ssm/lists/items?${params.toString()}`;
    const httpMethod = 'GET';
    return this.aldenService.callAlfresco(path, httpMethod);
  }

  /**
   * Obtiene los valores de un Data List de Alfresco, con soporte para jerarquía y ordenación.
   *
   * Este método recupera todos los elementos pertenecientes al Data List indicado por
   * `datalistName`. Permite además:
   *
   *  - Filtrar por la clave del elemento padre (`parentKey`) cuando el Data List tiene
   *    estructura jerárquica.
   *  - Filtrar por una clave maestra (`masterKey`) cuando el Data List depende de
   *    otra lista.
   *  - Ordenar los resultados por la clave del elemento (`sortByKey`).
   *
   * Devuelve una lista de pares clave‑valor extendidos (`KeyValueExtended`), que
   * incluyen información adicional del nodo además de la clave y el valor.
   *
   * @param datalistName Nombre del Data List de Alfresco a consultar.
   * @param parentKey (Opcional) Clave del nodo padre para filtrar elementos.
   * @param masterKey (Opcional) Clave maestra para filtrar elementos dependientes.
   * @param sortByKey (Opcional) Si true, ordena los resultados por la clave.
   * @param extraData (Opcional) Metadatos adicionales que se quieren obtener.
   * @returns Promise con los elementos del Data List en formato extendido.
   */
  getDataListValues(datalistName: string, parentKey?: string, masterKey?: string, sortByKey?: boolean, extraData?: string[]): Promise<KeyValueExtended<string, string>[]> {
    const postBody = {};
    const queryParams = {};
    const headerParams = {};
    const formParam = {}

    const params = new URLSearchParams();
    if (datalistName) params.append('datalistName', datalistName);
    if (parentKey) params.append('parentKey', parentKey);
    if (masterKey) params.append('masterKey', masterKey);
    params.append('sortByKey', sortByKey ? String(sortByKey) : "false");
    if (extraData) params.append('extraData', String(extraData));

    const url = `smtecno/ssm/lists/items?${params.toString()}`;

    const contentTypes = ['application/json'];
    const accepts = ['application/json'];

    return this.aldenService.callAlfresco(url,'GET', queryParams, headerParams, formParam, postBody, contentTypes, accepts);
  }

  /**
   * LEGACY
   * Obtiene la lista de tipos de documento utilizada antes del refactor
   * de Taxonomías de RRHH.
   *
   * Este método pertenece a la implementación antigua basada en Data Lists
   * y se mantiene únicamente por compatibilidad con funcionalidades previas.
   * En el nuevo modelo, los tipos de documento se obtienen desde las
   * taxonomías actualizadas y no mediante este mecanismo.
   *
   * @returns Promise<any> Promesa con la lista legacy de tipos de documento.
   */
  getTypeList(): Promise<any> {
    const postBody = {};
    const queryParams = {};
    const headerParams = {};
    const formParam = {};

    const contentTypes = ['application/json'];
    const accepts = ['application/json'];

    return this.aldenService
      .callAlfresco('rrhh-smt/create-rrhh/taxonomy/config/rrhh', 'GET', queryParams, headerParams, formParam, postBody, contentTypes, accepts);
  }
}
